import { FC, useEffect } from 'react';
import { Redirect } from 'react-router-dom';
import { IonBadge, IonButton, IonSelect, IonSelectOption, IonSpinner, IonToggle } from '@ionic/react';
import { InnerhivePage } from '../InnerhivePage';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { useSubscription } from '@meraki-internal/state';
import { createUseStyles } from 'react-jss';
import { AppPrincipal } from '../auth/AppPrincipal';
import { MixPanelEventEmitter } from '../metrics/MixPanelEventEmitter';
import { AILogsState } from './AILogsState';
import { AIConversationView } from './AIConversationView';
import { AI_CALL_SOURCES, AICallSource, IAICallSummary } from './AILogsClient';
import { sourceBadgeColor } from './sourceBadgeColor';

const useStyles = createUseStyles({
    filterBar: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 16,
        marginBottom: 12
    },
    sourceSelect: {
        width: 'auto',
        flex: '0 1 auto'
    },
    row: {
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 8,
        padding: '10px 8px',
        borderBottom: '1px solid var(--ion-color-step-150, #e0e0e0)',
        cursor: 'pointer'
    },
    rowMeta: {
        fontSize: 12,
        color: 'var(--ion-color-medium)'
    },
    rowUser: {
        fontWeight: 600,
        fontSize: 13
    },
    preview: {
        width: '100%',
        fontSize: 12,
        color: 'var(--ion-color-medium)',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
    },
    error: {
        color: 'var(--ion-color-danger)',
        margin: '12px 0'
    },
    empty: {
        color: 'var(--ion-color-medium)',
        margin: '24px 0',
        textAlign: 'center'
    },
    footer: {
        margin: '16px 0',
        textAlign: 'center'
    }
});

const CallRow: FC<{ call: IAICallSummary; onOpen: () => void }> = ({ call, onOpen }) => {
    const classes = useStyles();
    return (
        <div className={classes.row} onClick={onOpen} data-id="ai-log-row">
            <span className={classes.rowMeta}>{new Date(call.createdAt).toLocaleString()}</span>
            <span className={classes.rowUser}>{call.userEmail || call.userId}</span>
            <IonBadge color={sourceBadgeColor(call.source)}>{call.source}</IonBadge>
            <IonBadge color={call.status === 'ok' ? 'success' : call.status === 'incomplete' ? 'warning' : 'danger'}>{call.status}</IonBadge>
            <span className={classes.rowMeta}>{call.model}</span>
            <span className={classes.rowMeta}>{(call.latencyMs / 1000).toFixed(1)}s</span>
            {call.inputTokens !== undefined &&
                <span className={classes.rowMeta}>{call.inputTokens} in / {call.outputTokens ?? 0} out</span>
            }
            {call.toolNames && call.toolNames.length > 0 &&
                <span className={classes.rowMeta}>tools: {call.toolNames.join(', ')}</span>
            }
            {(call.inputPreview || call.outputPreview) &&
                <div className={classes.preview}>{call.inputPreview || call.outputPreview}</div>
            }
        </div>
    );
};

const FilterBar: FC<{ logsState: AILogsState }> = ({ logsState }) => {
    const classes = useStyles();
    const { filters } = logsState.state;
    return (
        <div className={classes.filterBar}>
            <IonSelect
                className={classes.sourceSelect}
                label="Source:"
                placeholder="All sources"
                value={filters.source ?? ''}
                interface="popover"
                onIonChange={e => {
                    const value: AICallSource | '' = e.detail.value;
                    void logsState.setSource(value === '' ? undefined : value);
                }}
            >
                <IonSelectOption value="">All sources</IonSelectOption>
                {AI_CALL_SOURCES.map(source => (
                    <IonSelectOption key={source} value={source}>{source}</IonSelectOption>
                ))}
            </IonSelect>
            <IonToggle
                checked={filters.includeTestUsers}
                onIonChange={e => void logsState.setIncludeTestUsers(e.detail.checked)}
            >
                Include test traffic
            </IonToggle>
        </div>
    );
};

const CallList: FC<{ logsState: AILogsState }> = ({ logsState }) => {
    const classes = useStyles();
    const { calls, isLoading, isLoadingMore, error, nextLink } = logsState.state;
    return (
        <>
            <FilterBar logsState={logsState} />

            {error && <div className={classes.error}>{error}</div>}
            {isLoading && <IonSpinner />}

            {!isLoading && calls.length === 0 && !error &&
                <div className={classes.empty}>No AI calls in the last 30 days.</div>
            }

            {calls.map(call => (
                <CallRow key={call.callId} call={call} onOpen={() => void logsState.openConversation(call)} />
            ))}

            {nextLink && !isLoading &&
                <div className={classes.footer}>
                    <IonButton fill="outline" onClick={() => void logsState.loadMore()} disabled={isLoadingMore}>
                        {isLoadingMore ? 'Loading...' : 'Load more'}
                    </IonButton>
                </div>
            }
        </>
    );
};

// PHI on this page is not replay-masked deliberately: only admins can render it, and admins are
// excluded from Mixpanel session replay entirely (SessionReplayService), so nothing here is
// recorded.
export const AILogsPage: FC = () => {
    const principal = useInstance(AppPrincipal);
    const logsState = useInstance(AILogsState);
    const mixpanel = useInstance(MixPanelEventEmitter);
    useSubscription(() => logsState);

    const isAdmin = principal.isAdmin;

    useEffect(() => {
        if (isAdmin) {
            mixpanel.track('AI Log Viewer Opened');
            // load() reports failures via state.error rather than throwing.
            void logsState.load();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAdmin]);

    if (!isAdmin) {
        return <Redirect to="/" />;
    }

    const { detailView, isDetailLoading, error } = logsState.state;

    return (
        <InnerhivePage title="AI Call Logs" data-id="ai-logs-page">
            {detailView
                ? <AIConversationView title={detailView.title} calls={detailView.calls} isLoading={isDetailLoading} error={error} onBack={logsState.closeDetail} />
                : <CallList logsState={logsState} />
            }
        </InnerhivePage>
    );
};
